import type { PropsWithChildren } from 'react'
import {
  type FC,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import type { Editor } from '@tiptap/react'
import { useEditor } from '@tiptap/react'

import { getDefaultExtensions } from './extensions'

const ProviderContext = createContext<{
  message: string
  text: string
  editor: Editor | null
}>({
  message: '',
  text: '',
  editor: null,
})

export const MessageEditorProvider: FC<PropsWithChildren> = ({ children }) => {
  const [message, setMessage] = useState('')
  const [text, setText] = useState('')

  const handleUpdate = useCallback(({ editor }: { editor: Editor }) => {
    setMessage(editor.getHTML())
    setText(editor.getText())
  }, [])

  const extensions = useMemo(() => {
    return getDefaultExtensions({
      placeholder: 'Type something...',
    })
  }, [])

  const editor = useEditor({
    extensions,
    content: message,
    onUpdate: handleUpdate,
  })

  const value = useMemo(
    () => ({
      message,
      text,
      editor,
    }),
    [message, text, editor],
  )

  return (
    <ProviderContext.Provider value={value}>
      {children}
    </ProviderContext.Provider>
  )
}

export const useMessageEditorContext = () => {
  const context = useContext(ProviderContext)

  if (!context) {
    throw new Error(
      'useMessageEditorContext must be used within a MessageEditorProvider',
    )
  }

  return context
}
